<template>
  <div class="news-page">
    <PageHeader
        :title="$t('routes.news')"
        :subtitle="$t('news.subtitle')"
        :breadcrumbItems="[{ text: $t('common.home'), path: $lp('/') }]"
        :currentText="$t('routes.news')"
    />

    <div class="page-container">
      <!-- 分类筛选 -->
      <div class="category-filter">
        <el-radio-group v-model="selectedCategory" @change="handleCategoryChange">
          <el-radio-button :label="null">{{ $t('news.allCategories') }}</el-radio-button>
          <el-radio-button v-for="category in categories" :key="category.id" :label="category.id">
            {{ category.name }}
          </el-radio-button>
        </el-radio-group>
      </div>

      <!-- 新闻列表 -->
      <div class="news-list">
        <div class="row">
          <div class="col" v-for="item in pagedData" :key="item._id">
            <ArticleCard
                :id="item._id"
                :title="item.title"
                :summary="getSummary(item.content)"
                :cover="item.cover"
                :date="item.editTime"
                :category="getCategoryName(item.category)"
                type="news"
                @view="viewDetail"
            />
          </div>
        </div>

        <!-- 空状态 -->
        <el-empty v-if="pagedData.length === 0" :description="$t('news.empty')"/>
      </div>

      <!-- 分页 -->
      <Pagination
          v-if="filteredData.length > pageSize"
          :total="filteredData.length"
          :current-page="currentPage"
          :page-size="pageSize"
          @current-change="changePage"
          @size-change="changePageSize"
      />
    </div>
  </div>
</template>

<script>
import {ref, computed} from 'vue';
import {useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {useNewsData} from '@/composables/useNewsData';
import {localePath} from '@/utils/locale';
import {getSummary} from '@/utils/helper';
import PageHeader from '@/components/PageHeader.vue';
import ArticleCard from '@/components/ArticleCard.vue';
import Pagination from '@/components/Pagination.vue';

export default {
  name: 'NewsView',
  components: {
    PageHeader,
    ArticleCard,
    Pagination
  },
  setup() {
    const router = useRouter();
    const {t, locale} = useI18n();
    // 按语言切换的静态新闻数据源（中文 news.js / 英文 news.en.js）
    const {items: newsItems, categories, getCategoryName: getCategoryNameByLocale} = useNewsData();
    const selectedCategory = ref(null);
    const currentPage = ref(1);
    const pageSize = ref(10);

    // 按分类过滤（本地静态数据）
    const filteredData = computed(() => {
      if (selectedCategory.value === null) return newsItems.value;
      return newsItems.value.filter(item => item.category === selectedCategory.value);
    });

    // 当前页数据（本地分页）
    const pagedData = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value;
      return filteredData.value.slice(start, start + pageSize.value);
    });

    // 获取分类名称
    const getCategoryName = (categoryId) => {
      return getCategoryNameByLocale(categoryId) || t('common.uncategorized');
    };

    // 查看详情（按当前语言生成路径）
    const viewDetail = (id) => {
      router.push(localePath(`/news/${id}`, locale.value));
    };

    // 处理分类变化，重置到第一页
    const handleCategoryChange = () => {
      currentPage.value = 1;
    };

    // 切换页码
    const changePage = (page) => {
      currentPage.value = page;
    };

    // 修改每页条数
    const changePageSize = (size) => {
      pageSize.value = size;
      currentPage.value = 1;
    };

    return {
      pagedData,
      filteredData,
      currentPage,
      pageSize,
      selectedCategory,
      categories,
      getCategoryName,
      getSummary,
      viewDetail,
      handleCategoryChange,
      changePage,
      changePageSize
    };
  }
}
</script>

<style lang="scss" scoped>
.news-page {
  .category-filter {
    margin-bottom: 30px;
  }

  .news-list {
    margin-bottom: 30px;
    min-height: 300px;

    .row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 30px;

      @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
      }

      .col {
        margin-bottom: 30px;
      }
    }
  }
}
</style>
